import { FormattedMessage, useIntl } from "react-intl";
import { elementDisplayName, elementNameId } from "@/lib/elements";
import type { ExtractionDoc } from "./ExtractionDialog";
import { addedSkills, movedPowers, type ExtractionJobRecord, type ProcessResult } from "./extraction-run-changes";

// `documents` is the run's whole accounting, so every number the panel shows is counted from it
// rather than read from a total.
// @external-adr GENESIS-0039-an-extraction-run-is-accounted-for-once-per-document
const DOCUMENT_OUTCOMES = [
  "analysed",
  "already_analysed",
  "nothing_found",
  "unreadable",
  "failed",
] as const;

type DocumentOutcome = (typeof DOCUMENT_OUTCOMES)[number];

/** `unreadable` is the user's file to fix, `failed` is ours to retry — both read as "not analysed". */
const UNUSABLE_OUTCOMES: ReadonlySet<DocumentOutcome> = new Set<DocumentOutcome>(["unreadable", "failed"]);

const OUTCOME_LABELS: Record<DocumentOutcome, { id: string; defaultMessage: string }> = {
  analysed: { id: "save.extraction.outcome.analysed", defaultMessage: "Analysed" },
  already_analysed: { id: "save.extraction.outcome.alreadyAnalysed", defaultMessage: "Already analysed" },
  nothing_found: { id: "save.extraction.outcome.nothingFound", defaultMessage: "Analysed, nothing found" },
  unreadable: { id: "save.extraction.outcome.unreadable", defaultMessage: "No readable text" },
  failed: { id: "save.extraction.outcome.failed", defaultMessage: "Analysis failed" },
};

const OUTCOME_COUNT_LABELS: Record<DocumentOutcome, { id: string; defaultMessage: string }> = {
  analysed: { id: "save.extraction.count.analysed", defaultMessage: "{count} analysed" },
  already_analysed: { id: "save.extraction.count.alreadyAnalysed", defaultMessage: "{count} already analysed" },
  nothing_found: { id: "save.extraction.count.nothingFound", defaultMessage: "{count} nothing found" },
  unreadable: { id: "save.extraction.count.unreadable", defaultMessage: "{count} unreadable" },
  failed: { id: "save.extraction.count.failed", defaultMessage: "{count} failed" },
};

/**
 * An unknown status is reported as `failed` rather than rendered as-is. The wire value is an
 * untranslated server string, and showing one in a German dialog is the defect this panel fixes.
 */
function toOutcome(status: string | null | undefined): DocumentOutcome {
  const normalised = status?.toLowerCase() ?? "";
  return (DOCUMENT_OUTCOMES as readonly string[]).includes(normalised)
    ? (normalised as DocumentOutcome)
    : "failed";
}

export function ExtractionResultView({
  result,
  job,
  docs = [],
  unscoredPowers,
}: {
  result: ProcessResult;
  job: ExtractionJobRecord | null;
  docs?: readonly ExtractionDoc[];
  unscoredPowers?: number | null;
}) {
  const intl = useIntl();
  const newSkills = job ? addedSkills(job, intl) : null;
  const changedPowers = job ? movedPowers(job) : null;
  // genesis records each document's name with the run (genesis#429); `docs` names a result written
  // without it.
  const nameByRef = new Map(docs.map((d) => [d.id, d.name]));

  const outcomes = result.documents.map((d) => toOutcome(d.status));
  const unusable = outcomes.filter((outcome) => UNUSABLE_OUTCOMES.has(outcome)).length;
  // The headline stays "abgeschlossen" while anything worked, because the job is detached: people
  // arrive here from a notification minutes later, and whether it finished is what they came to see.
  // Only a run where nothing survived turns it red. With no documents at all — neither the event nor
  // the job carried them — the run-level status is all that is left to go on.
  const failedRun =
    outcomes.length === 0
      ? !["ok", "success"].includes(result.status?.toLowerCase() ?? "")
      : unusable === outcomes.length;
  const nothingContributed =
    outcomes.includes("nothing_found") && !outcomes.includes("analysed");
  const breakdown = DOCUMENT_OUTCOMES.map((outcome) => ({
    outcome,
    count: outcomes.filter((o) => o === outcome).length,
  })).filter((bucket) => bucket.count > 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {/* Kennung je Zustand: die Zeile steht in BEIDEN Fällen, nur ihr Text
            unterscheidet sie. Ein Test soll den Ausgang nicht am Wort ablesen. */}
        <span
          id={failedRun ? "analysis-statusfailed" : "analysis-statusok"}
          className={`text-smycles-meta font-medium ${failedRun ? "text-danger" : "text-ink"}`}
        >
          {failedRun ? (
            <FormattedMessage
              id="save.extraction.analysisFailed"
              defaultMessage="Analysis failed."
            />
          ) : (
            <FormattedMessage
              id="save.extraction.analysisComplete"
              defaultMessage="Analysis complete."
            />
          )}
        </span>
        {/* The canonical chip fill with amber text: the run finished, some documents just did not
            come through — "needs a look", not a second failure signal. */}
        {unusable > 0 && !failedRun ? (
          <span className="inline-flex items-center rounded-full bg-ink/[0.06] px-2 py-0.5 text-smycles-label text-ink-attention">
            <FormattedMessage
              id="save.extraction.notAnalysedChip"
              defaultMessage="{count} not analysed"
              values={{ count: unusable }}
            />
          </span>
        ) : null}
      </div>
      {breakdown.length > 0 ? (
        <div className="box-flat bg-page px-3 py-2 text-smycles-meta text-ink-muted">
          {breakdown
            .map((bucket) => intl.formatMessage(OUTCOME_COUNT_LABELS[bucket.outcome], { count: bucket.count }))
            .join(" · ")}
        </div>
      ) : null}
      {!failedRun && newSkills ? (
        <div id="analysis-new-skills">
          <div className="smycles-field-label mb-1 text-ink-muted">
            <FormattedMessage
              id="save.extraction.newSkillsHeading"
              defaultMessage="Newly detected skills{count, plural, =0 {} other { (#)}}"
              values={{ count: newSkills.length }}
            />
          </div>
          {newSkills.length === 0 ? (
            <div className="text-smycles-label text-ink-muted">
              <FormattedMessage
                id="save.extraction.noNewSkills"
                defaultMessage="No new entries - everything was probably already in your skills."
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {newSkills.map((s, i) => (
                <span
                  key={`${s.skill}-${i}`}
                  id={`analysis-skill-${s.skill}`}
                  className="box-flat inline-flex items-center gap-1.5 bg-page px-2.5 py-1 text-smycles-label text-ink"
                >
                  {s.skill}
                  {s.level ? <span className="text-ink-muted">· {s.level}</span> : null}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
      {!failedRun && changedPowers ? (
        <div id="analysis-new-powers">
          <div className="smycles-field-label mb-1 text-ink-muted">
            <FormattedMessage
              id="save.extraction.powersUpdatedHeading"
              defaultMessage="Powers updated{count, plural, =0 {} other { (#)}}"
              values={{ count: changedPowers.length }}
            />
          </div>
          {changedPowers.length === 0 ? (
            <div id="analysis-no-new-powers" className="text-smycles-label text-ink-muted">
              <FormattedMessage
                id="save.extraction.noPowerChanges"
                defaultMessage="No changes – no power value moved."
              />
            </div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {changedPowers.map((p, i) => (
                <span
                  key={`${p.element}-${i}`}
                  id={`analysis-power-${p.element}`}
                  className="box-flat inline-flex items-center gap-1.5 bg-page px-2.5 py-1 text-smycles-label text-ink"
                >
                  {intl.formatMessage({
                    id: elementNameId(p.element),
                    defaultMessage: elementDisplayName(p.element),
                  })}
                  <span className="text-ink-muted">· {p.score}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}
      {!failedRun && unscoredPowers != null && unscoredPowers > 0 ? (
        <div className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="save.extraction.powersNoIndication"
            defaultMessage="{count, plural, one {# power with no indication.} other {# powers with no indication.}}"
            values={{ count: unscoredPowers }}
          />
        </div>
      ) : null}
      {!failedRun && nothingContributed ? (
        <div className="smycles-field-meta text-ink-muted">
          <FormattedMessage
            id="save.extraction.nothingFoundHint"
            defaultMessage="Try references, appraisals or feedback – text that describes how you work."
          />
        </div>
      ) : null}
      {result.documents.length > 0 ? (
        <div className="space-y-1.5">
          {result.documents.map((d, i) => {
            const outcome = toOutcome(d.status);
            return (
              <div key={`${d.ref}-${i}`} className="flex items-center justify-between gap-2 text-smycles-label text-ink">
                <span className="truncate">{d.fileName ?? nameByRef.get(d.ref) ?? d.ref}</span>
                <span id={`analysis-docoutcome-${outcome}-${d.ref}`} className="shrink-0 text-ink-muted">
                  {intl.formatMessage(OUTCOME_LABELS[outcome])}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
